import {EditorState, type Extension, StateField, Transaction} from '@codemirror/state'

import {getNodesContainingRange} from '../../language-tools/syntax-tree/node-range-utils'
import {getMarkdownTree} from './markdown-parser'

export interface SelectionContextValue {
  inBold: boolean
  inItalics: boolean
  inInlineCode: boolean
}

export const initialSelectionContext: SelectionContextValue = {
  inBold: false,
  inItalics: false,
  inInlineCode: false,
}

/**
 * Determines the Markdown context of the current selection.
 * Returns an object indicating if the selection is within formatting.
 */
function getSelectionContext(state: EditorState): SelectionContextValue {
  const context: SelectionContextValue = {...initialSelectionContext}
  const tree = getMarkdownTree(state)
  const selection = state.selection.main
  const containedNodes = getNodesContainingRange(tree, selection.from, selection.to, true)
  for (const node of containedNodes) {
    switch (node.type) {
      case 'strong':
        context.inBold = true
        break
      case 'emphasis':
        context.inItalics = true
        break
      case 'inlineCode':
        context.inInlineCode = true
        break
    }
  }

  return context
}

/**
 * State field for accessing the current selection context. The context is updated synchronously
 * when the selection changes.
 */
const selectionContextField = StateField.define<SelectionContextValue>({
  create: getSelectionContext,
  update: (prev, tr) => {
    if (tr.selection) {
      return getSelectionContext(tr.state)
    }
    return prev
  },
})

/**
 * Extension to track selection context that updates as the selection changes.
 */
export const selectionContextExtension: Extension = selectionContextField

/** Get the selection context from the editor state. Throws an error if `selectionContextExtension` is not installed. */
export function getCurrentSelectionContext(state: EditorState): SelectionContextValue {
  return state.field(selectionContextField)
}

/**
 * Check if the selection context changed in this transaction.
 */
export function selectionContextChanged(transaction: Transaction): boolean {
  if (!transaction.selection) return false

  const oldContext = transaction.startState.field(selectionContextField)
  const newContext = transaction.state.field(selectionContextField)

  return !contextEquals(oldContext, newContext)
}

/**
 * Compare two selection contexts for equality
 */
function contextEquals(a: SelectionContextValue, b: SelectionContextValue): boolean {
  const keysA = Object.keys(a) as Array<keyof SelectionContextValue>
  return keysA.every(key => a[key] === b[key])
}
